import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, Clock, Briefcase } from "lucide-react";

function Opt({ active, onClick, icon: Icon, title, sub, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`w-full flex items-start gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${active ? "border-brand bg-brand/5" : "border-slate-200 hover:border-brand/50"}`}>
      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${active ? "text-brand" : "text-slate-400"}`} />
      <span className="min-w-0"><span className="block font-medium text-slate-800 truncate">{title}</span>{sub && <span className="block text-xs text-slate-500 truncate">{sub}</span>}</span>
    </button>
  );
}

const jobLabel = (j) => `#${j.jobNumber ?? "—"} · ${j.address}`;
const jobSub = (j, showDate) => `${showDate ? `${j.date} · ` : ""}${j.startTime || ""}${j.endTime ? `–${j.endTime}` : ""}`;

function JobGroup({ title, jobs, form, pick, showDate, testid }) {
  if (!jobs.length) return null;
  return (
    <div data-testid={testid}>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">{title}</p>
      <div className="space-y-1.5">
        {jobs.map((j) => <Opt key={j.id} active={form.target === "job" && form.jobId === j.id} onClick={() => pick(j)} icon={Briefcase} title={jobLabel(j)} sub={jobSub(j, showDate)} testid={`att-add-job-${j.id}`} />)}
      </div>
    </div>
  );
}

export function AddHoursTarget({ form, setForm, t }) {
  const [lists, setLists] = useState({ mine: [], day: [], search: [] });
  const [q, setQ] = useState("");
  useEffect(() => {
    if (!form.workerId || !form.date) return undefined;
    const h = setTimeout(() => {
      client.get("/attendance/manual-jobs", { params: { workerId: form.workerId, date: form.date, q } })
        .then((r) => setLists(r.data)).catch(() => setLists({ mine: [], day: [], search: [] }));
    }, q ? 300 : 0);
    return () => clearTimeout(h);
  }, [form.workerId, form.date, q]);
  if (!form.workerId) return null;
  const pick = (j) => setForm({ ...form, target: "job", jobId: j.id, ...(j.startTime && j.endTime && form.target !== "job" ? { startTime: j.startTime, endTime: j.endTime } : {}) });
  return (
    <div className="space-y-2.5" data-testid="att-add-target">
      <Label>{t("att.addTo", "Add these hours to")}</Label>
      <Opt active={form.target === "general"} onClick={() => setForm({ ...form, target: "general", jobId: null })} icon={Clock} title={t("att.generalTimer", "General timer")} sub={t("att.generalSub", "The cleaner's day timer \u00b7 shows on Timesheets")} testid="att-add-target-general" />
      <div className="max-h-[260px] overflow-y-auto space-y-2.5 pr-1">
        <JobGroup title={t("att.theirJobs", "Their jobs that day")} jobs={lists.mine} form={form} pick={pick} testid="att-add-mine" />
        <JobGroup title={t("att.dayJobs", "Other jobs that day")} jobs={lists.day} form={form} pick={pick} testid="att-add-day" />
        {!lists.mine.length && !lists.day.length && <p className="text-xs text-slate-400" data-testid="att-add-nojobs">{t("att.noJobsDay", "No jobs booked that day.")}</p>}
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("att.searchJobs", "Search all jobs \u2014 number, address or client")} className="pl-8 h-9" data-testid="att-add-job-search" />
        </div>
        <JobGroup title={t("att.searchResults", "Search results")} jobs={lists.search} form={form} pick={pick} showDate testid="att-add-search" />
      </div>
    </div>
  );
}
